import React, {use, createContext, useState, useEffect} from "react";
import PropTypes from "prop-types";
import {useMutation, useLazyQuery} from "@apollo/client";
import GetGroupsDetails from "./gql/GetGroupsDetails.graphql";
import GetUsers from "./gql/GetAllUsers.graphql";
import GetUsersInGroup from "./gql/GetUsersInGroup.graphql";
import GetUsersGroupsDetails from "./gql/GetUsersGroupsDetails.graphql";
import GetTrueRegscoutUserCount from "./gql/GetTrueRegscoutUserCount.graphql";
import ToggleUserEnablement from "./gql/ToggleEnablement.graphql";
import axios from "axios";
import {useLogin} from "../components/login/LoginProvider";
import {ALERT} from "../environment";
import {isEmpty} from "underscore";

const AUTH_ENDPOINT = process.env.AUTH_ENDPOINT;

export const UsersContext = createContext(null);

export const UsersProvider = ({children}) => {
  const [users, setUsers] = useState();
  const [groups, setGroups] = useState();
  const [usersGroupsDetails, setUsersGroupsDetails] = useState();
  const [groupsDetails, setGroupsDetails] = useState();
  const [trueRegscoutUserCount, setTrueRegscoutUserCount] = useState();
  const {user} = useLogin();

  const getGroups = async () => {
    const response = await axios.get(`${AUTH_ENDPOINT}/manage/groups`, {
      headers: {
        Authorization: `Bearer ${user.IdToken}`,
      },
    });
    setGroups(response.data);
  };

  const [getUsersQuery, getUsersQueryMethods] = useLazyQuery(GetUsers);

  useEffect(() => {
    if (getUsersQueryMethods.data) {
      setUsers(getUsersQueryMethods.data.users);
      getGroups();
    }
  }, [getUsersQueryMethods.data]);

  useEffect(() => {
    getUsersQuery();
  }, []);

  const [getUsersInGroupQuery, getUsersInGroupQueryMethods] = useLazyQuery(GetUsersInGroup);

  useEffect(() => {
    if (getUsersInGroupQueryMethods.data) {
      setUsers(getUsersInGroupQueryMethods.data.groupUsers);
    }
  }, [getUsersInGroupQueryMethods.data]);

  const [getGroupsDetailsQuery, getGroupsDetailsQueryMethods] = useLazyQuery(GetGroupsDetails, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (getGroupsDetailsQueryMethods.data) {
      setGroupsDetails(getGroupsDetailsQueryMethods.data.groupsDetails);
    }
  }, [getGroupsDetailsQueryMethods.data]);

  const [getUsersGroupsDetailsQuery, getUsersGroupsDetailsQueryMethods] = useLazyQuery(
    GetUsersGroupsDetails,
    {
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (getUsersGroupsDetailsQueryMethods.data) {
      setUsersGroupsDetails(getUsersGroupsDetailsQueryMethods.data.usersGroupsDetails);
    }
  }, [getUsersGroupsDetailsQueryMethods.data]);

  const [getTrueRegscoutUserCountQuery, getTrueRegscoutUserCountQueryMethods] = useLazyQuery(
    GetTrueRegscoutUserCount,
    {
      fetchPolicy: "network-only",
    },
  );

  useEffect(() => {
    if (getTrueRegscoutUserCountQueryMethods.data) {
      setTrueRegscoutUserCount(getTrueRegscoutUserCountQueryMethods.data.getTrueRegscoutUserCount);
    }
  }, [getTrueRegscoutUserCountQueryMethods.data]);

  const resendConfirmation = async (username) => {
    try {
      const response = await axios.post(
        `${AUTH_ENDPOINT}/manage/user/revalidate`,
        {username},
        {
          headers: {
            Authorization: `Bearer ${user.IdToken}`,
          },
        },
      );
      if (response.status === 200) ALERT.success("Confirmation email successfully resent.");
    } catch (error) {
      ALERT.error(error?.response?.data?.message ?? "Confirmation email did not send.");
    }
  };

  const [toggleEnablementMutation, toggleEnablementQuery] = useMutation(ToggleUserEnablement);

  useEffect(() => {
    if (toggleEnablementQuery.data) {
      const enabled = toggleEnablementQuery.data.toggleUserEnablement.Enabled;
      ALERT.success(`User successfully ${enabled ? "enabled" : "disabled"}`);
      getUsersQueryMethods.refetch();
    }
  }, [toggleEnablementQuery.data, getUsersQueryMethods]);

  const toggleUserEnablement = (username) => {
    toggleEnablementMutation({variables: {username}});
  };

  if (isEmpty(users) || isEmpty(groups)) return null;

  return (
    <UsersContext
      value={{
        getUsersQuery,
        getUsersQueryMethods,
        getUsersInGroupQuery,
        getUsersInGroupQueryMethods,
        users,
        usersGroupsDetails,
        setUsersGroupsDetails,
        getUsersGroupsDetailsQuery,
        getUsersGroupsDetailsQueryMethods,
        groupsDetails,
        setGroupsDetails,
        getGroupsDetailsQuery,
        getGroupsDetailsQueryMethods,
        getTrueRegscoutUserCountQuery,
        getTrueRegscoutUserCountQueryMethods,
        trueRegscoutUserCount,
        resendConfirmation,
        toggleEnablementQuery,
        toggleUserEnablement,
        groups,
      }}
    >
      {children}
    </UsersContext>
  );
};

UsersProvider.propTypes = {
  children: PropTypes.any,
};

export const useUsers = () => {
  return use(UsersContext);
};
